import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import GenderPopup from "../components/PopUps/GenderPopup";
import CareEconomyPopup from "../components/PopUps/CareEconomyPopup";
import MentalHealthPopup from "../components/PopUps/MentalHealthPopup";
import TrendsPopup from "../components/PopUps/TrendsPopup";
import ClimateChangePopup from "../components/PopUps/ClimateChangePopup";
import AboutPopup from "../components/PopUps/AboutPopup";
import NavigatingPrototypePopup from "../components/PopUps/NavigatingPrototypePopup";
import Icon from "../components/ui/Icon";
import Footer from "../components/Footer";

const ANIMATION_PLAYED_KEY = "visualMapAnimationPlayed";
const NAVIGATE_POPUP_AUTO_SHOWN_KEY = "navigatePopupAutoShown";

const VisualMap = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isGenderModalOpen, setIsGenderModalOpen] = useState(false);
  const [isCareEconomyModalOpen, setIsCareEconomyModalOpen] = useState(false);
  const [isMentalHealthModalOpen, setIsMentalHealthModalOpen] = useState(false);
  const [isTrendsModalOpen, setIsTrendsModalOpen] = useState(false);
  const [isClimateChangeModalOpen, setIsClimateChangeModalOpen] =
    useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isNavigateModalOpen, setIsNavigateModalOpen] = useState(false);
  const [isInteractionAllowed, setIsInteractionAllowed] = useState(false);
  const [clickOrigin, setClickOrigin] = useState("center");
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [scrollY, setScrollY] = useState(0);
  const navigateIconRef = React.useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const openPopup = (setter) => (e) => {
    if (e && e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      setClickOrigin(
        `${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px`,
      );
    } else {
      setClickOrigin("center");
    }
    setter(true);
  };

  useEffect(() => {
    // Check if the loader has already been shown in this session
    const hasLoaderShown = sessionStorage.getItem("intro_loader_shown");

    if (hasLoaderShown) {
      setIsLoading(false);
    } else {
      // Show loading GIF for 2-3 seconds only on first visit
      const loadingTimer = setTimeout(() => {
        setIsLoading(false);
        sessionStorage.setItem("intro_loader_shown", "true");
      }, 1200);

      return () => clearTimeout(loadingTimer);
    }
  }, []);

  useEffect(() => {
    // Only proceed with animations after loading is complete
    if (isLoading) return;

    // Check if animation has been played before in this session
    const hasPlayed = sessionStorage.getItem(ANIMATION_PLAYED_KEY) === "true";

    if (hasPlayed) {
      setShouldAnimate(false);
      setIsInteractionAllowed(true);
    } else {
      // First time loading - play animation
      setShouldAnimate(true);
      // Mark animation as played after the longest animation completes
      // The last element (bottomIcons) has step 13, so: 13 * 900ms + 1500ms = 13200ms
      const animationDuration = 13 * 900 + 1500;
      setTimeout(() => {
        sessionStorage.setItem(ANIMATION_PLAYED_KEY, "true");

        // Auto-open the navigate popup after animations complete (only once per session)
        // For large screens, this happens after the animation sequence
        const hasAutoShown =
          sessionStorage.getItem(NAVIGATE_POPUP_AUTO_SHOWN_KEY) === "true";
        if (isLargeScreen && !hasAutoShown) {
          if (navigateIconRef.current) {
            const rect = navigateIconRef.current.getBoundingClientRect();
            setClickOrigin(
              `${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px`,
            );
          }
          setIsNavigateModalOpen(true);
          sessionStorage.setItem(NAVIGATE_POPUP_AUTO_SHOWN_KEY, "true");
        }
        setIsInteractionAllowed(true);
      }, animationDuration);
    }

    // Auto-open for small screens after 1.5 seconds (only once per session)
    const hasAutoShown =
      sessionStorage.getItem(NAVIGATE_POPUP_AUTO_SHOWN_KEY) === "true";
    if (!isLargeScreen && !hasAutoShown) {
      const timer = setTimeout(() => {
        const stillNotShown =
          sessionStorage.getItem(NAVIGATE_POPUP_AUTO_SHOWN_KEY) === "true";
        if (!stillNotShown) {
          if (navigateIconRef.current) {
            const rect = navigateIconRef.current.getBoundingClientRect();
            setClickOrigin(
              `${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px`,
            );
          }
          setIsNavigateModalOpen(true);
          sessionStorage.setItem(NAVIGATE_POPUP_AUTO_SHOWN_KEY, "true");
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isLargeScreen]);

  const STAGGER_MS = 900;
  const seqStyle = (step, duration = 1500) => ({
    "--map-seq-delay": `${step * STAGGER_MS}ms`,
    "--map-seq-duration": `${duration}ms`,
  });

  const styleFor = (key, duration) =>
    shouldAnimate ? seqStyle(revealOrder[key], duration) : undefined;

  const handleScrollAction = () => {
    const currentScrollY =
      window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    if (currentScrollY > 50) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const nextSection = document.getElementById("scroll-view");
      if (nextSection) {
        const rect = nextSection.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetY = rect.top + scrollTop - 20; // 20px offset for better visual alignment
        window.scrollTo({ top: targetY, behavior: "smooth" });
      } else {
        window.scrollBy({ top: window.innerHeight * 0.8, behavior: "smooth" });
      }
    }
  };

  const revealOrder = {
    middle: 0,
    connect1: 1,
    gender: 2,
    connect2: 3,
    careEconomy: 4,
    connect3: 5,
    trends: 6,
    connect4: 7,
    mentalHealth: 8,
    connect5: 9,
    climateChange: 10,
    caregiverTypes: 11,
    grayBg: 12,
    bottomIcons: 13,
  };

  const playBounce = (event, state) => {
    const el = event.currentTarget;

    el.classList.remove("map-hover-enter", "map-hover-leave");
    void el.offsetWidth;
    el.classList.add(state);
  };

  const bounceHandlers = {
    onMouseEnter: (e) => playBounce(e, "map-hover-enter"),
    onMouseLeave: (e) => playBounce(e, "map-hover-leave"),
  };

  const handleExploreGender = () => {
    sessionStorage.setItem("viewed_gender_popup", "true");
    setIsGenderModalOpen(false);
    navigate("/tabs?tab=gender");
  };

  const handleExploreCareEconomy = () => {
    sessionStorage.setItem("viewed_care_economy_popup", "true");
    setIsCareEconomyModalOpen(false);
    navigate("/tabs?tab=care-economy");
  };

  const handleExploreMentalHealth = () => {
    sessionStorage.setItem("viewed_mental_health_popup", "true");
    setIsMentalHealthModalOpen(false);
    navigate("/tabs?tab=mental-health");
  };

  const handleExploreTrends = () => {
    sessionStorage.setItem("viewed_trends_popup", "true");
    setIsTrendsModalOpen(false);
    navigate("/tabs?tab=trends");
  };

  const handleExploreClimateChange = () => {
    sessionStorage.setItem("viewed_climate_change_popup", "true");
    setIsClimateChangeModalOpen(false);
    navigate("/tabs?tab=climate-change");
  };

  if (isLoading) {
    return (
      <main id="top" className="pt-5">
        <div className="fixed inset-0 flex items-center justify-center bg-white z-[9999]">
          <img
            src="/assets/gif/loading.gif"
            alt="Loading..."
            className="w-auto h-auto max-w-md max-h-md"
          />
        </div>
      </main>
    );
  }

  return (
    <main id="top" className="pt-5">
      <div className="flex justify-center items-center lg:hidden">
        <div className="relative [zoom:0.7] xs:[zoom:0.8] xss:[zoom:1]">
          <img
            src="/assets/svgs/mobile-visual-map/mb-bg-lines.svg"
            alt="mobile-visual-map"
            className="z-0"
          />
          <Link to="/types-of-caregivers">
            <img
              src="/assets/svgs/mobile-visual-map/t-twoc.svg"
              alt="mobile-visual-map"
              className="z-30 absolute left-[44.4%] top-[42.5%] -translate-x-1/2 -translate-y-1/2 hidden xxs:block cursor-pointer"
            />
            <img
              src="/assets/svgs/mobile-visual-map/m-twoc.svg"
              alt="mobile-visual-map"
              className="z-30 absolute left-[44.6%] top-[42.45%] -translate-x-1/2 -translate-y-1/2 xxs:hidden cursor-pointer"
            />
          </Link>
          <img
            src="/assets/svgs/mobile-visual-map/gender.svg"
            alt="mobile-visual-map"
            className="z-30 absolute left-[30.9%] top-[20.6%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            onClick={openPopup(setIsGenderModalOpen)}
          />
          <img
            src="/assets/svgs/mobile-visual-map/climate.svg"
            alt="mobile-visual-map"
            className="z-30 absolute left-[18.5%] top-[51.8%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            onClick={openPopup(setIsClimateChangeModalOpen)}
          />
          <img
            src="/assets/svgs/mobile-visual-map/care-eco.svg"
            alt="mobile-visual-map"
            className="z-30 absolute right-[8%] xxs:right-[9.3%] top-[23.8%] xxs:top-[24%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            onClick={openPopup(setIsCareEconomyModalOpen)}
          />
          <img
            src="/assets/svgs/mobile-visual-map/mental.svg"
            alt="mobile-visual-map"
            className="z-30 absolute right-[-3%] xxs:right-[-1.8%] top-[50%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            onClick={openPopup(setIsMentalHealthModalOpen)}
          />
          <img
            src="/assets/svgs/mobile-visual-map/trend.svg"
            alt="mobile-visual-map"
            className="z-30 absolute left-[58%] bottom-[3%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            onClick={openPopup(setIsTrendsModalOpen)}
          />
        </div>
      </div>
      <div
        id="scroll-view"
        className="flex justify-center items-center scroll-mt-5 lg:hidden mt-20 mb-[90%] xss:mb-[60%] relative [zoom:0.6] xs:[zoom:0.8] sm:[zoom:1] max-w-[510px] mx-auto"
      >
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/mobile-connect-line.svg"
          alt="mobile-visual-map"
          className="z-0"
        />
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/l-care-eco.svg"
          alt="mobile-visual-map"
          className="z-10 absolute left-[49.6%] top-[12.3%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleExploreCareEconomy}
        />
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/l-gender.svg"
          alt="mobile-visual-map"
          className="z-10 absolute left-[41.4%] top-[30.8%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleExploreGender}
        />
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/l-mental.svg"
          alt="mobile-visual-map"
          className="z-10 absolute left-[48%] top-[50.7%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleExploreMentalHealth}
        />
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/l-climate.svg"
          alt="mobile-visual-map"
          className="z-10 absolute left-[50.9%] top-[72.7%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleExploreClimateChange}
        />
        <img
          src="/assets/svgs/mobile-visual-map/scroll-view/l-trends.svg"
          alt="mobile-visual-map"
          className="z-10 absolute left-[50%] bottom-[-19.65%] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          onClick={handleExploreTrends}
        />
      </div>

      <section className="p-1 hidden lg:block">
        <div className="relative mx-auto aspect-[1070/865] max-w-[1070px] max-h-[863px] [zoom:0.8] lg:[zoom:0.75] xl:[zoom:0.9] xxl:[zoom:1]">
          <img
            src="/assets/svgs/gray_bg_lines.svg"
            alt=""
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 h-full w-full object-contain z-50 map-seq`}
            style={styleFor("grayBg", 900)}
          />

          <img
            src="/assets/svgs/middle_woc.svg"
            alt="The Web of Care"
            className={`absolute left-[35.2%] top-[30.2%] -translate-x-1/2 -translate-y-1/2 map-seq`}
            style={styleFor("middle")}
          />

          <div>
            <img
              src="/assets/svgs/connect_line_1.svg"
              alt=""
              aria-hidden="true"
              className={`pointer-events-none absolute left-[32%] top-[24.5%] z-40 map-seq`}
              style={styleFor("connect1")}
            />
            <img
              src="/assets/svgs/connect_line_2.svg"
              alt=""
              aria-hidden="true"
              className={`pointer-events-none absolute left-[50.6%] top-[26.5%] z-40 map-seq`}
              style={styleFor("connect2")}
            />
            <img
              src="/assets/svgs/connect_line_3.svg"
              alt=""
              aria-hidden="true"
              className={`pointer-events-none absolute left-[55%] top-[37.8%] z-40 map-seq`}
              style={styleFor("connect3")}
            />
            <img
              src="/assets/svgs/connect_line_5.svg"
              alt=""
              aria-hidden="true"
              className={`pointer-events-none absolute left-[24.2%] top-[41%] z-40 map-seq`}
              style={styleFor("connect5")}
            />
            <img
              src="/assets/svgs/connect_line_4.svg"
              alt=""
              aria-hidden="true"
              className={`pointer-events-none absolute right-[49%] top-[47.2%] z-40 map-seq`}
              style={styleFor("connect4")}
            />
          </div>

          <div
            className={`${!isInteractionAllowed ? "pointer-events-none" : ""}`}
          >
            <Link to="/types-of-caregivers">
              <img
                src="/assets/svgs/types_of_caregiver.svg"
                alt="Types of caregivers"
                className={`absolute right-[31%] top-[40.3%] z-40 map-hover-bounce hover:cursor-pointer map-seq `}
                style={styleFor("caregiverTypes")}
                {...bounceHandlers}
              />
            </Link>

            <button
              onClick={openPopup(setIsGenderModalOpen)}
              className="absolute left-[-0.5%] top-[6.5%] z-20 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/svgs/gender.svg"
                alt="Gender and caregiving"
                className={`map-hover-bounce hover:cursor-pointer map-seq`}
                style={styleFor("gender")}
                {...bounceHandlers}
              />
            </button>

            <button
              onClick={openPopup(setIsCareEconomyModalOpen)}
              className="absolute right-[-4%] top-[2.5%] z-30 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/svgs/care_economy.svg"
                alt="Care economy"
                className={`map-hover-bounce hover:cursor-pointer map-seq`}
                style={styleFor("careEconomy")}
                {...bounceHandlers}
              />
            </button>

            <button
              onClick={openPopup(setIsTrendsModalOpen)}
              className="absolute right-[-15%] bottom-[2.6%] z-30 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/svgs/trends.svg"
                alt="Emerging trends in caregiving"
                className={`map-hover-bounce hover:cursor-pointer map-seq`}
                style={styleFor("trends")}
                {...bounceHandlers}
              />
            </button>

            <button
              onClick={openPopup(setIsMentalHealthModalOpen)}
              className="absolute left-[30%] top-[65%] z-30 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/svgs/mental_health.svg"
                alt="Caregiving and mental health"
                className={`map-hover-bounce hover:cursor-pointer map-seq`}
                style={styleFor("mentalHealth")}
                {...bounceHandlers}
              />
            </button>

            <button
              onClick={openPopup(setIsClimateChangeModalOpen)}
              className="absolute left-[-9.5%] bottom-[5.5%] z-30 border-0 bg-transparent p-0 cursor-pointer"
            >
              <img
                src="/assets/svgs/climate_change.svg"
                alt="Climate change and caregiving"
                className={`map-hover-bounce hover:cursor-pointer map-seq`}
                style={styleFor("climateChange")}
                {...bounceHandlers}
              />
            </button>
          </div>
        </div>
      </section>

      <div
        className={`fixed bottom-28 xss:bottom-20 left-4 z-[100] lg:hidden ${
          isLargeScreen && !isInteractionAllowed ? "pointer-events-none" : ""
        }`}
        style={isLargeScreen ? styleFor("bottomIcons", 1500) : undefined}
      >
        <button
          type="button"
          onClick={handleScrollAction}
          className={`cursor-pointer lg:hidden transition-all duration-500 ${
            scrollY > 50 ? "rotate-90" : "-rotate-90"
          }`}
        >
          <Icon
            src="/assets/svgs/back_btn.svg"
            alt="scroll down"
            className="animate-bounce-down w-[45px] h-[45px]"
          />
        </button>
      </div>

      <div
        className={`fixed bottom-10 xss:bottom-16 left-4 right-4 z-50 map-seq flex items-end justify-end gap-4 ${
          isLargeScreen && !isInteractionAllowed ? "pointer-events-none" : ""
        }`}
        style={isLargeScreen ? styleFor("bottomIcons", 1500) : undefined}
      >
        <button
          ref={navigateIconRef}
          type="button"
          className="cursor-pointer"
          onClick={openPopup(setIsNavigateModalOpen)}
        >
          <Icon
            src="/assets/svgs/navigate.svg"
            alt="navigate"
            className="w-[45px] h-[45px]"
          />
        </button>
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0"
          onClick={openPopup(setIsAboutModalOpen)}
        >
          <Icon
            src="/assets/svgs/about.svg"
            alt="about"
            className="w-[45px] h-[45px]"
          />
        </button>
      </div>

      <div className="w-full">
        <Footer />
      </div>

      <GenderPopup
        open={isGenderModalOpen}
        onClose={() => setIsGenderModalOpen(false)}
        onExplore={handleExploreGender}
        transformOrigin={clickOrigin}
      />

      <CareEconomyPopup
        open={isCareEconomyModalOpen}
        onClose={() => setIsCareEconomyModalOpen(false)}
        onExplore={handleExploreCareEconomy}
        transformOrigin={clickOrigin}
      />

      <MentalHealthPopup
        open={isMentalHealthModalOpen}
        onClose={() => setIsMentalHealthModalOpen(false)}
        onExplore={handleExploreMentalHealth}
        transformOrigin={clickOrigin}
      />

      <TrendsPopup
        open={isTrendsModalOpen}
        onClose={() => setIsTrendsModalOpen(false)}
        onExplore={handleExploreTrends}
        transformOrigin={clickOrigin}
      />

      <ClimateChangePopup
        open={isClimateChangeModalOpen}
        onClose={() => setIsClimateChangeModalOpen(false)}
        onExplore={handleExploreClimateChange}
        transformOrigin={clickOrigin}
      />

      <AboutPopup
        open={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
        transformOrigin={clickOrigin}
      />

      <NavigatingPrototypePopup
        open={isNavigateModalOpen}
        onClose={() => setIsNavigateModalOpen(false)}
        transformOrigin={clickOrigin}
      />
    </main>
  );
};

export default VisualMap;
